import * as React from "react";
import { Check, ChevronsUpDown, Search, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function CustomerSearchSelect({ 
  customers = [], 
  value, 
  onValueChange, 
  placeholder = "Kunde suchen...",
  disabled = false 
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // 06/2026: Vorzugspreis-Kennzeichnung — Team erkennt Rabatt sofort.
  const discountLabel = (c) => {
    if (!c) return null;
    const pct = Number(c.discount_percent || 0);
    if (pct > 0) return `-${pct % 1 === 0 ? pct : pct.toFixed(1)}%`;
    if (c.has_individual_prices) return "Vorzugspreis";
    return null;
  };
  const DiscountBadge = ({ customer }) => {
    const label = discountLabel(customer);
    if (!label) return null;
    return (
      <span
        className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold align-middle whitespace-nowrap"
        data-testid="customer-discount-badge"
        title="Kunde hat einen Vorzugspreis / Rabatt"
      >
        💚 {label}
      </span>
    );
  };

  // Find selected customer
  const selectedCustomer = customers.find((customer) => customer.id === value);

  // Filter customers based on search
  const filteredCustomers = React.useMemo(() => {
    if (!searchQuery) return customers.slice(0, 50); // Show first 50 if no search
    
    const query = searchQuery.toLowerCase();
    return customers.filter((customer) => {
      const fullName = `${customer.first_name} ${customer.last_name}`.toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = (customer.phone || customer.mobile || '').toLowerCase();
      return fullName.includes(query) || email.includes(query) || phone.includes(query);
    }).slice(0, 50); // Limit results
  }, [customers, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
        >
          {selectedCustomer ? (
            <span className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              {selectedCustomer.first_name} {selectedCustomer.last_name}
              <DiscountBadge customer={selectedCustomer} />
              {selectedCustomer.status === 'inactive' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600" data-testid="selected-customer-inactive-badge">Inaktiv</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Name, E-Mail oder Telefon eingeben..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList>
            <CommandEmpty>
              {searchQuery ? "Kein Kunde gefunden." : "Bitte Suchbegriff eingeben..."}
            </CommandEmpty>
            <CommandGroup>
              {filteredCustomers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={customer.id}
                  onSelect={() => {
                    onValueChange(customer.id);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === customer.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col flex-1">
                    <span className={cn("font-medium", customer.status === 'inactive' && "text-gray-400")}>
                      {customer.first_name} {customer.last_name}
                      {customer.status === 'inactive' && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 align-middle">Inaktiv</span>
                      )}
                      <span className="ml-2"><DiscountBadge customer={customer} /></span>
                    </span>
                    <span className="text-xs text-gray-500">
                      {customer.email || 'Keine E-Mail'} 
                      {customer.phone && ` • ${customer.phone}`}
                    </span>
                  </div>
                  <span className="ml-2 text-xs text-gray-400">
                    {customer.points || 0} Pkt
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
